import { gql } from "@apollo/client";
import { getSalaryCashComp } from "@asmbl/shared/compensation";
import { CashBandName, CurrencyCode } from "@asmbl/shared/constants";
import { add, formatCurrency, zero } from "@asmbl/shared/money";
import { TableCell, TableRow, Typography } from "@mui/material";
import makeStyles from "../../../../makeStylesV4Compat";
import { formatNumber } from "accounting";
import { useCompStructure } from "src/components/CompStructureContext";
import {
  CashCompType,
  CompComponent,
  CompUnit,
  RecItemInput,
  RecItemType,
  TargetIncreaseRow_employee,
  TargetIncreaseRow_position,
} from "../../../../__generated__/graphql";
import { useTrack } from "../../../../analytics";
import { ADJUSTED_CASH_BAND_FIELDS } from "../../../../fragments";
import { isBandPointDefined } from "../../../../models/BandPoint";
import {
  getCashRecItem,
  getDraftSalaryIncrease,
} from "../../../../models/CompRecommendation";
import { GRAY_6 } from "../../../../theme";
import { ArrayValue } from "../../../../utils";
import { CondensedBandVisualization } from "../../../CompSlider/CondensedBandVisualization";
import { NullCompSlider } from "../../../CompSlider/NullCompSlider";
import { useCurrencies } from "../../../CurrenciesContext";
import {
  ConfigurableSalaryInputCell,
  InputOption,
} from "../../Cells/ConfigurableTargetInputCell";
import { useCompRecData } from "../../CompCycleSingleRecModal/CompRecommendationContext";
import { CompCycleLineItemRowButton } from "../CompCycleLineItemRowButton";
import { useConnectorStyles, useRecItemRowStyles } from "./styles";

const useStyles = makeStyles(() => ({
  labelContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  sliderCell: {
    borderTop: `1px solid ${GRAY_6} !important`,
  },
  noteContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type CashCompensation = ArrayValue<
  TargetIncreaseRow_employee["activeCashCompensation"]
>;
interface Props {
  employee: TargetIncreaseRow_employee;
  label: string;
  recommendationType: RecItemType;
  promotedPosition: TargetIncreaseRow_position | null;
  component: CompComponent;
  cashCompType: CashCompType;
  onChangeRecommendationItem: (
    newRecommendationItem: RecItemInput | null
  ) => unknown;
}

export function TargetIncreaseRow({
  employee,
  label,
  promotedPosition,
  recommendationType,
  cashCompType,
  onChangeRecommendationItem,
  component,
}: Props): JSX.Element {
  const { defaultCurrencyCode } = useCurrencies();
  const { compStructure } = useCompStructure();
  const { revisedRecommendation, submittedRecommendation } = useCompRecData();

  // target increases are only inputted as annual values so we'll use the employee's annual cash equivalent
  const currentPay = getSalaryCashComp(employee.activeCashCompensation);
  const currentAnnualCash = currentPay?.annualCashEquivalent;
  const currency = (currentAnnualCash?.currency ??
    employee.activeEmployment?.payCurrencyCode ??
    defaultCurrencyCode) as CurrencyCode;
  // look for a draft item first, then check for a saved item
  const submittedItem = submittedRecommendation?.items.get(recommendationType);

  const targetIncreaseItem =
    revisedRecommendation?.items.get(recommendationType);

  const salaryIncrease = getDraftSalaryIncrease(
    revisedRecommendation,
    currency,
    employee.activeEmployment?.payPeriod,
    currentPay,
    compStructure?.workingHoursPerYear ?? 0
  );

  // target increases are only inputted as annual values
  const newPay =
    currentAnnualCash != null
      ? add(currentAnnualCash, salaryIncrease.annualCashEquivalent)
      : currentAnnualCash;

  const cashValue =
    targetIncreaseItem != null && newPay != null
      ? getCashRecItem(
          targetIncreaseItem,
          newPay,
          compStructure?.workingHoursPerYear ?? 0
        ).recommendedCashValue
      : zero(currency);

  const percentValue =
    targetIncreaseItem?.recommendedPercentValue != null
      ? targetIncreaseItem.recommendedPercentValue
      : cashValue && newPay && newPay.value !== 0
        ? (100 * cashValue.value) / newPay.value
        : 0;

  const alternateValueLabel =
    targetIncreaseItem?.unitType !== CompUnit.PERCENT_OF_SALARY
      ? `${formatNumber(percentValue, 2)}%`
      : cashValue != null
        ? formatCurrency(cashValue, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })
        : "";

  const { Track } = useTrack({
    compensationComponent: component,
    employeeId: employee.id,
  });
  const recItemStyle = useRecItemRowStyles();
  const connectorStyle = useConnectorStyles();
  const classes = useStyles();

  const adjustedCashBands = promotedPosition
    ? promotedPosition.adjustedCashBands
    : employee.adjustedCashBands;

  const adjustedTargetBand = adjustedCashBands?.find(
    (b) => b.name === CashBandName[cashCompType]
  );

  const targetCashComp = employee.activeCashCompensation?.find(
    (c) => c.type === cashCompType
  );

  const draftCompUnit =
    targetIncreaseItem?.unitType ?? targetCashComp?.unit ?? CompUnit.CASH;

  const defaultInputOption =
    draftCompUnit === CompUnit.PERCENT_OF_SALARY
      ? InputOption.PERCENT_OF_SALARY
      : InputOption.CASH_VALUE;

  return (
    <Track>
      <TableRow aria-level={3} className={recItemStyle.root}>
        <TableCell role="gridcell" padding="none">
          <div className={connectorStyle.line} />
        </TableCell>
        <TableCell
          role="gridcell"
          colSpan={5}
          padding="none"
          className={recItemStyle.textCell}
        >
          <div className={classes.labelContainer}>
            <Typography className={recItemStyle.rowLabel}>{label}</Typography>
            <Typography className={recItemStyle.extraRowText} component="span">
              {formatCurrentTarget(targetCashComp)}
            </Typography>
          </div>
        </TableCell>
        <ConfigurableSalaryInputCell
          value={targetIncreaseItem ? cashValue ?? null : null}
          baseValue={newPay ?? null}
          payCurrencyCode={currency}
          onChange={(cash, percent, option) => {
            onChangeRecommendationItem({
              recommendationType: recommendationType,
              recommendedCashValue:
                option === InputOption.CASH_VALUE ? cash : undefined,
              recommendedPercentValue:
                option === InputOption.PERCENT_OF_SALARY ? percent : undefined,
              note: targetIncreaseItem?.note,
              unitType:
                option === InputOption.PERCENT_OF_SALARY
                  ? CompUnit.PERCENT_OF_SALARY
                  : CompUnit.CASH,
            });
          }}
          inputOptions={[
            { label: "% of New Salary", value: InputOption.PERCENT_OF_SALARY },
            { label: "Cash Value", value: InputOption.CASH_VALUE },
          ]}
          defaultInputOption={defaultInputOption}
          hasUnpublishedChanges={hasUnpublishedChanges(
            submittedItem,
            targetIncreaseItem
          )}
        />

        <TableCell role="gridcell" className={classes.sliderCell}>
          {adjustedTargetBand &&
          adjustedTargetBand.bandPoints.some(isBandPointDefined) ? (
            <CondensedBandVisualization
              value={cashValue ?? zero(defaultCurrencyCode)}
              bandPoints={adjustedTargetBand.bandPoints}
              position={
                promotedPosition ?? employee.activeEmployment?.position ?? null
              }
              size="wide"
              outOfRangeStyle="band"
              valueLabel={label}
              showHourly={false}
            />
          ) : (
            <NullCompSlider variant="condensedWide" />
          )}
        </TableCell>
        <TableCell
          role="gridcell"
          className={recItemStyle.noteTextCell}
          colSpan={2}
        >
          <div className={classes.noteContainer}>
            <Typography className={recItemStyle.extraRowText} component="span">
              {alternateValueLabel}
            </Typography>
            <CompCycleLineItemRowButton
              disabled={
                targetIncreaseItem?.recommendedCashValue === undefined &&
                targetIncreaseItem?.recommendedPercentValue === undefined
              }
              employee={employee}
              note={targetIncreaseItem?.note ?? null}
              onClick={(note: string) =>
                onChangeRecommendationItem({
                  ...targetIncreaseItem,
                  recommendationType,
                  note,
                })
              }
            />
          </div>
        </TableCell>
      </TableRow>
    </Track>
  );
}

TargetIncreaseRow.fragments = {
  employee: gql`
    ${ADJUSTED_CASH_BAND_FIELDS}
    ${CompCycleLineItemRowButton.fragments.employee}
    fragment TargetIncreaseRow_employee on Employee2 {
      id
      ...CompCycleLineItemRowButton_employee
      activeEmployment {
        id
        payCurrencyCode
        payPeriod
        position {
          id
          name
          level
          type
          ladder {
            id
            name
            department {
              id
              name
            }
          }
        }
      }
      activeCashCompensation(currencyCode: $currencyCode) {
        activeAt
        type
        annualCashEquivalent
        hourlyCashEquivalent
        percentOfSalary
        unit
        employeeId
      }
      adjustedCashBands {
        id
        ...AdjustedCashBandFields
      }
    }
  `,

  position: gql`
    ${ADJUSTED_CASH_BAND_FIELDS}
    fragment TargetIncreaseRow_position on Position {
      id
      name
      level
      type
      ladder {
        id
        name
        department {
          id
          name
        }
      }
      adjustedCashBands(
        currencyCode: $currencyCode
        marketId: $marketId
        locationGroupId: $locationGroupId
      ) {
        id
        ...AdjustedCashBandFields
      }
    }
  `,
};

function hasUnpublishedChanges(
  submittedItem: RecItemInput | undefined,
  revisedItem: RecItemInput | undefined
): boolean {
  return revisedItem?.unitType !== CompUnit.PERCENT_OF_SALARY
    ? revisedItem?.recommendedCashValue?.value !==
        submittedItem?.recommendedCashValue?.value
    : revisedItem.recommendedPercentValue !==
        submittedItem?.recommendedPercentValue;
}

function formatCurrentTarget(targetCash: CashCompensation | undefined): string {
  if (targetCash === undefined) return "";

  const cash = formatCurrency(targetCash.annualCashEquivalent, {
    maximumFractionDigits: 0,
  });
  const percent = formatNumber(targetCash.percentOfSalary, 2);
  return `Current target: ${cash} / ${percent}%`;
}
